/**
 * DecisionFlow — OverlayAttributionManager
 * data-fit LLC · Confidential
 * Domain: P3-07 Overlay Attribution
 * Tier: Enterprise — feature_gate_key = 'feature.tier.overlay'
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §8
 * API Contract: api_contract_v1_32.md §53
 * Wizard substages:
 *   overlay_rule_definition · overlay_rule_approval · overlay_credit_validation
 *
 * Two tabs:
 *   Tab 1 — Rules: select overlay position → create / edit / submit / approve
 *            overlay_attribution_rule records with segment/BU scoping
 *   Tab 2 — Results: view overlay_commission_result rows for a calculation run
 *
 * DDL ground truth (phase3_plan_spec §8.2):
 *   overlay_attribution_rule:
 *     rule_id bigint PK, tenant_id uuid,
 *     overlay_position_id uuid FK (the specialist/overlay rep),
 *     plan_component_id int FK (the overlay rep's rate source),
 *     attribution_weight numeric(5,4) NN — 0.0001 to 1.0000,
 *     effective_from date NN, effective_to date nullable,
 *     applies_to_segment_id int nullable — NULL = all segments,
 *     applies_to_bu_id int nullable — NULL = all BUs,
 *     is_active bool NN,
 *     submitted_by uuid, submitted_at timestamptz,
 *     approved_by uuid, approved_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *
 *   overlay_commission_result:
 *     overlay_result_id uuid PK, calculation_run_id uuid,
 *     overlay_position_id uuid, primary_position_id uuid,
 *     credit_id uuid, fiscal_period_id int, overlay_rule_id bigint,
 *     attributed_deal_value numeric(18,4),
 *     overlay_commission_rate numeric(10,6),
 *     overlay_commission_amount numeric(18,4),
 *     payout_currency char(3), created_at timestamptz
 *
 * Engine specificity note (OA2):
 *   Rules are resolved most-specific-first:
 *   segment+BU specific > segment-only > BU-only > catch-all (both NULL)
 *   UI surfaces this ordering so comp_admin understands rule precedence.
 *
 * API surface (api_contract_v1_32.md §53):
 *   POST /api/v1/overlay-attribution-rules
 *   GET  /api/v1/overlay-attribution-rules?overlay_position_id={id}
 *   GET  /api/v1/overlay-attribution-rules/{rule_id}
 *   PUT  /api/v1/overlay-attribution-rules/{rule_id}    — raw fetch
 *   POST /api/v1/overlay-attribution-rules/{rule_id}/submit
 *   POST /api/v1/overlay-attribution-rules/{rule_id}/approve
 *   GET  /api/v1/overlay-commission-results?calculation_run_id={id}&overlay_position_id={id}
 *
 * Architecture rules:
 *   - useAuth() for { user } — no token in props (ARCH-2)
 *   - apiGet / apiPost from client — PUT via raw fetch
 *   - tenant_id never in any request body
 *   - No window.prompt / window.confirm — inline confirmation state
 *   - ApprovalPanel for overlay_attribution_rule (submitted_by real column)
 *   - onReject={null} — reject endpoint not in §53
 *   - 403 FEATURE_UNAVAILABLE → GateStatusBanner
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array (wizard context)
 *   embedded         — bool  — reserved; these managers are tenant-scoped
 *                              (COMPENSATION POLICIES nav, not PlanDetailView)
 *   readOnly         — bool  — true when viewing archived context
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'rules',   label: 'Attribution Rules' },
  { key: 'results', label: 'Run Results'        },
];

const EMPTY_RULE_FORM = {
  overlay_position_id:    '',
  plan_component_id:      '',
  attribution_weight:     '',
  effective_from:         '',
  effective_to:           '',
  applies_to_segment_id:  '',
  applies_to_bu_id:       '',
  is_active:              true,
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (!entity) return 'draft';
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

function fmtPct(val) {
  if (val === null || val === undefined || val === '') return '—';
  return `${(Number(val) * 100).toFixed(2)}%`;
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtRate(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toFixed(6);
}

// Describe the scope of a rule for display
function scopeLabel(rule) {
  const hasSeg = rule.applies_to_segment_id != null;
  const hasBU  = rule.applies_to_bu_id != null;
  if (hasSeg && hasBU)  return `Segment ${rule.applies_to_segment_id} + BU ${rule.applies_to_bu_id}`;
  if (hasSeg)           return `Segment ${rule.applies_to_segment_id} (all BUs)`;
  if (hasBU)            return `BU ${rule.applies_to_bu_id} (all segments)`;
  return 'All segments & BUs (catch-all)';
}

// Specificity rank for display ordering (higher = more specific = evaluated first by engine)
function specificityRank(rule) {
  const hasSeg = rule.applies_to_segment_id != null;
  const hasBU  = rule.applies_to_bu_id != null;
  if (hasSeg && hasBU) return 3;
  if (hasSeg || hasBU) return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OverlayAttributionManager({ mode, period, wizardSteps, embedded = false, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.overlay')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [activeTab, setActiveTab]                   = useState('rules');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // Positions (all) — used to identify overlay positions and populate selectors
  const [positions, setPositions]                   = useState([]);
  const [positionsLoading, setPositionsLoading]     = useState(false);

  // Plan components — for plan_component_id selector
  const [components, setComponents]                 = useState([]);

  // Segments + BUs for scope selectors
  const [segments, setSegments]                     = useState([]);
  const [businessUnits, setBusinessUnits]           = useState([]);

  // Selected overlay position
  const [selectedPositionId, setSelectedPositionId] = useState('');

  // Rules for selected position
  const [rules, setRules]                           = useState([]);
  const [rulesLoading, setRulesLoading]             = useState(false);
  const [rulesError, setRulesError]                 = useState('');

  // Selected rule detail
  const [selectedRule, setSelectedRule]             = useState(null);
  const [detailLoading, setDetailLoading]           = useState(false);
  const [actionLoading, setActionLoading]           = useState(false);
  const [actionError, setActionError]               = useState('');

  // Rule form
  const [showRuleForm, setShowRuleForm]             = useState(false);
  const [ruleForm, setRuleForm]                     = useState(EMPTY_RULE_FORM);
  const [ruleSaving, setRuleSaving]                 = useState(false);
  const [ruleFormError, setRuleFormError]           = useState('');
  const [editingRuleId, setEditingRuleId]           = useState(null);

  // Results tab
  const [runIdInput, setRunIdInput]                 = useState('');
  const [overlayPosIdInput, setOverlayPosIdInput]   = useState('');
  const [results, setResults]                       = useState([]);
  const [resultsLoading, setResultsLoading]         = useState(false);
  const [resultsError, setResultsError]             = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Boot: positions, components, segments, BUs
  // ---------------------------------------------------------------------------

  const loadBootData = useCallback(async () => {
    if (!user) return;
    setPositionsLoading(true);
    try {
      // Load positions
      const posData = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(posData) ? posData : posData?.positions || posData?.data || []);

      // Load plan components
      const periodId = period?.fiscal_period_id;
      const compUrl = periodId
        ? `/api/v1/plan-components?fiscal_period_id=${periodId}`
        : '/api/v1/plan-components';
      const compData = await apiGet(compUrl).catch(() => ({ data: [] }));
      setComponents(Array.isArray(compData) ? compData : compData?.data || compData?.plan_components || []);

      // Load segments and BUs — non-blocking
      const segData = await apiGet('/api/v1/segments').catch(() => []);
      setSegments(Array.isArray(segData) ? segData : segData?.data || []);

      const buData = await apiGet('/api/v1/business-units').catch(() => []);
      setBusinessUnits(Array.isArray(buData) ? buData : buData?.data || []);

    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
      }
    } finally {
      setPositionsLoading(false);
    }
  }, [user, period]);

  useEffect(() => { loadBootData(); }, [loadBootData]);

  // ---------------------------------------------------------------------------
  // Load rules for selected overlay position
  // ---------------------------------------------------------------------------

  const loadRules = useCallback(async (positionId) => {
    if (!positionId) return;
    setRulesLoading(true);
    setRulesError('');
    setRules([]);
    try {
      const data = await apiGet(`/api/v1/overlay-attribution-rules?overlay_position_id=${positionId}`);
      setRules(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setRulesError(getErrorMessage(error, 'Unable to load attribution rules.'));
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPositionId) loadRules(selectedPositionId);
  }, [selectedPositionId, loadRules]);

  // ---------------------------------------------------------------------------
  // Load rule detail
  // ---------------------------------------------------------------------------

  const loadRuleDetail = useCallback(async (ruleId) => {
    setDetailLoading(true);
    setActionError('');
    try {
      const data = await apiGet(`/api/v1/overlay-attribution-rules/${ruleId}`);
      setSelectedRule(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load rule detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Rule form helpers
  // ---------------------------------------------------------------------------

  const updateRuleForm = useCallback((field, value) =>
    setRuleForm((prev) => ({ ...prev, [field]: value })), []);

  const resetRuleForm = useCallback(() => {
    setRuleForm((prev) => ({ ...EMPTY_RULE_FORM, overlay_position_id: selectedPositionId }));
    setRuleFormError('');
    setEditingRuleId(null);
  }, [selectedPositionId]);

  const validateRuleForm = useCallback(() => {
    if (!ruleForm.overlay_position_id) return 'Overlay position is required.';
    if (!ruleForm.plan_component_id)   return 'Plan component (rate source) is required.';
    const w = Number(ruleForm.attribution_weight);
    if (!ruleForm.attribution_weight || isNaN(w) || w <= 0 || w > 1)
      return 'Attribution weight must be between 0.0001 and 1.0000.';
    if (!ruleForm.effective_from)      return 'Effective from date is required.';
    if (ruleForm.effective_to && ruleForm.effective_to < ruleForm.effective_from)
      return 'Effective to must be after effective from.';
    return '';
  }, [ruleForm]);

  // ---------------------------------------------------------------------------
  // Create rule
  // ---------------------------------------------------------------------------

  const createRule = useCallback(async () => {
    const err = validateRuleForm();
    if (err) { setRuleFormError(err); return; }
    setRuleSaving(true);
    setRuleFormError('');
    try {
      const payload = {
        overlay_position_id:   ruleForm.overlay_position_id,
        plan_component_id:     Number(ruleForm.plan_component_id),
        attribution_weight:    ruleForm.attribution_weight,
        effective_from:        ruleForm.effective_from,
        effective_to:          ruleForm.effective_to || null,
        applies_to_segment_id: ruleForm.applies_to_segment_id
                                 ? Number(ruleForm.applies_to_segment_id) : null,
        applies_to_bu_id:      ruleForm.applies_to_bu_id
                                 ? Number(ruleForm.applies_to_bu_id) : null,
        is_active:             ruleForm.is_active,
      };
      const created = await apiPost('/api/v1/overlay-attribution-rules', payload);
      await loadRules(selectedPositionId);
      setShowRuleForm(false);
      resetRuleForm();
      showToast('Attribution rule created.');
      const newId = created?.rule_id;
      if (newId) await loadRuleDetail(newId);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'OVERLAY_WEIGHT_EXCEEDS_ONE')
        setRuleFormError('Attribution weight cannot exceed 1.0000 (100%).');
      else if (code === 'OVERLAY_WEIGHT_INVALID')
        setRuleFormError('Attribution weight must be greater than zero.');
      else
        setRuleFormError(getErrorMessage(error, 'Unable to create attribution rule.'));
    } finally {
      setRuleSaving(false);
    }
  }, [loadRuleDetail, loadRules, resetRuleForm, ruleForm, selectedPositionId, showToast, validateRuleForm]);

  // ---------------------------------------------------------------------------
  // Update rule (PUT — raw fetch, not approved only)
  // ---------------------------------------------------------------------------

  const updateRule = useCallback(async () => {
    if (!editingRuleId) return;
    const err = validateRuleForm();
    if (err) { setRuleFormError(err); return; }
    setRuleSaving(true);
    setRuleFormError('');
    try {
      const res = await fetch(`/api/v1/overlay-attribution-rules/${editingRuleId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_component_id:     Number(ruleForm.plan_component_id),
          attribution_weight:    ruleForm.attribution_weight,
          effective_from:        ruleForm.effective_from,
          effective_to:          ruleForm.effective_to || null,
          applies_to_segment_id: ruleForm.applies_to_segment_id
                                   ? Number(ruleForm.applies_to_segment_id) : null,
          applies_to_bu_id:      ruleForm.applies_to_bu_id
                                   ? Number(ruleForm.applies_to_bu_id) : null,
          is_active:             ruleForm.is_active,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED')
          throw new Error('This rule is already approved and cannot be edited.');
        if (code === 'OVERLAY_WEIGHT_EXCEEDS_ONE')
          throw new Error('Attribution weight cannot exceed 1.0000.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadRuleDetail(editingRuleId);
      await loadRules(selectedPositionId);
      setShowRuleForm(false);
      resetRuleForm();
      showToast('Attribution rule updated.');
    } catch (error) {
      setRuleFormError(error.message || 'Unable to update rule.');
    } finally {
      setRuleSaving(false);
    }
  }, [editingRuleId, loadRuleDetail, loadRules, resetRuleForm, ruleForm, selectedPositionId, showToast, validateRuleForm]);

  const openEditRule = useCallback((rule) => {
    setEditingRuleId(rule.rule_id);
    setRuleForm({
      overlay_position_id:   rule.overlay_position_id || '',
      plan_component_id:     rule.plan_component_id != null ? String(rule.plan_component_id) : '',
      attribution_weight:    rule.attribution_weight != null ? String(rule.attribution_weight) : '',
      effective_from:        fmtDate(rule.effective_from) === '—' ? '' : fmtDate(rule.effective_from),
      effective_to:          rule.effective_to ? fmtDate(rule.effective_to) : '',
      applies_to_segment_id: rule.applies_to_segment_id != null ? String(rule.applies_to_segment_id) : '',
      applies_to_bu_id:      rule.applies_to_bu_id != null ? String(rule.applies_to_bu_id) : '',
      is_active:             rule.is_active !== false,
    });
    setRuleFormError('');
    setShowRuleForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Approval actions
  // ---------------------------------------------------------------------------

  const submitRule = useCallback(async () => {
    if (!selectedRule) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/overlay-attribution-rules/${selectedRule.rule_id}/submit`, {});
      await loadRuleDetail(selectedRule.rule_id);
      await loadRules(selectedPositionId);
      showToast('Rule submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit rule.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadRuleDetail, loadRules, selectedPositionId, selectedRule, showToast]);

  const approveRule = useCallback(async () => {
    if (!selectedRule) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/overlay-attribution-rules/${selectedRule.rule_id}/approve`, {});
      await loadRuleDetail(selectedRule.rule_id);
      await loadRules(selectedPositionId);
      showToast('Rule approved.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setActionError('You submitted this rule — a different user must approve it.');
      else if (code === 'OVERLAY_ATTRIBUTION_RULE_NOT_APPROVED')
        setActionError('Rule must be submitted before it can be approved.');
      else
        setActionError(getErrorMessage(error, 'Unable to approve rule.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadRuleDetail, loadRules, selectedPositionId, selectedRule, showToast]);

  // ---------------------------------------------------------------------------
  // Results tab
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!runIdInput.trim()) { setResultsError('Calculation run ID is required.'); return; }
    setResultsLoading(true);
    setResultsError('');
    setResults([]);
    try {
      const params = new URLSearchParams({ calculation_run_id: runIdInput.trim() });
      if (overlayPosIdInput.trim()) params.set('overlay_position_id', overlayPosIdInput.trim());
      const data = await apiGet(`/api/v1/overlay-commission-results?${params}`);
      setResults(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setResultsError(getErrorMessage(error, 'Unable to load overlay commission results.'));
    } finally {
      setResultsLoading(false);
    }
  }, [overlayPosIdInput, runIdInput]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => {
      acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`;
      return acc;
    }, {}),
    [positions]
  );

  const componentLabelById = useMemo(
    () => components.reduce((acc, c) => {
      acc[String(c.plan_component_id)] =
        c.role_code || c.role_id || `Component ${c.plan_component_id}`;
      return acc;
    }, {}),
    [components]
  );

  // Sort rules by specificity (most specific first — matches engine OA2 ordering)
  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => specificityRank(b) - specificityRank(a)),
    [rules]
  );

  const unapprovedCount = useMemo(
    () => rules.filter((r) => !r.approved_by).length,
    [rules]
  );

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) {
    return (
      <GateStatusBanner
        feature="Overlay Attribution"
        message="Overlay attribution requires the Enterprise tier (feature.tier.overlay). Contact your account manager to enable this feature."
      />
    );
  }

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Overlay Attribution</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Rule detail view
  // ---------------------------------------------------------------------------

  if (selectedRule) {
    const state   = deriveState(selectedRule);
    const isDraft = state === 'draft';

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => {
                setSelectedRule(null);
                setActionError('');
                setShowRuleForm(false);
                resetRuleForm();
              }}>
              ← Back to rules
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              Attribution Rule
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {positionNameById[String(selectedRule.overlay_position_id)] || selectedRule.overlay_position_id} ·
              Weight {fmtPct(selectedRule.attribution_weight)} ·
              {scopeLabel(selectedRule)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={state} />
            {isDraft && (
              <button type="button" className={btnSecondary} onClick={() => openEditRule(selectedRule)}>
                Edit
              </button>
            )}
          </div>
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Rule detail card */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Rule details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Overlay position</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium">
                {positionNameById[String(selectedRule.overlay_position_id)] || selectedRule.overlay_position_id}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Plan component (rate source)</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {componentLabelById[String(selectedRule.plan_component_id)] || selectedRule.plan_component_id}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Attribution weight</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium text-tok-base">
                {fmtPct(selectedRule.attribution_weight)}
                <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))] font-normal">
                  of credit_reporting applied to overlay rate table
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Active</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedRule.is_active ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective from</dt>
              <dd className="text-[rgb(var(--color-text))]">{fmtDate(selectedRule.effective_from)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective to</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedRule.effective_to ? fmtDate(selectedRule.effective_to) : 'Open-ended'}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Segment scope</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedRule.applies_to_segment_id != null
                  ? (segments.find((s) => s.segment_id === selectedRule.applies_to_segment_id)?.segment_name
                      || `Segment ${selectedRule.applies_to_segment_id}`)
                  : <span className="italic text-[rgb(var(--color-text-muted))]">All segments</span>}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">BU scope</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedRule.applies_to_bu_id != null
                  ? (businessUnits.find((b) => b.business_unit_id === selectedRule.applies_to_bu_id)?.business_unit_name
                      || `BU ${selectedRule.applies_to_bu_id}`)
                  : <span className="italic text-[rgb(var(--color-text-muted))]">All BUs</span>}
              </dd>
            </div>
          </dl>

          {/* Specificity callout */}
          <div className="mt-1 rounded-lg bg-[rgb(var(--color-surface-muted))]/60 border border-[rgb(var(--color-border))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span className="font-medium text-[rgb(var(--color-text))]">Rule precedence: </span>
            The engine applies the most specific matching rule (segment+BU &gt; segment-only &gt; BU-only &gt; catch-all).
            This rule scope: <span className="font-medium text-[rgb(var(--color-text))]">{scopeLabel(selectedRule)}</span>
          </div>
        </div>

        {/* Inline edit form */}
        {showRuleForm && editingRuleId === selectedRule.rule_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit rule</h3>
            {ruleFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {ruleFormError}
              </div>
            )}
            {renderRuleFormFields({ ruleForm, updateRuleForm, components, segments, businessUnits, positions, inputClass, labelClass, disablePosition: true })}
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updateRule} disabled={ruleSaving}>
                {ruleSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowRuleForm(false); resetRuleForm(); }} disabled={ruleSaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Approval panel */}
        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="plan"
            currentState={state}
            submittedBy={selectedRule.submitted_by ?? null}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitRule : null}
            onApprove={state === 'pending_approval' ? approveRule : null}
            onReject={null}
            approvedBy={selectedRule.approved_by ?? null}
            approvedAt={selectedRule.approved_at ?? null}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-07 · Enterprise
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Overlay Attribution</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure commission attribution rules for specialist overlay reps (SEs, industry specialists,
          CS overlays). Attribution weight determines what fraction of each deal value is used to compute
          the overlay rep's commission. The primary rep's payout is unaffected.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}>
            {tab.label}
            {tab.key === 'rules' && unapprovedCount > 0 && (
              <span className="ml-1.5 inline-block rounded-full bg-[rgb(var(--color-warning))]/20 text-[rgb(var(--color-warning))] text-tok-xs px-1.5 py-0.5">
                {unapprovedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Rules tab ── */}
      {activeTab === 'rules' && (
        <div className="flex flex-col gap-4">

          {/* Overlay position selector */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 flex items-end gap-3 flex-wrap shadow-sm">
            <label className={labelClass} style={{ flex: '1', minWidth: '240px' }}>
              Overlay position
              {positionsLoading && <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</span>}
              <select className={inputClass} value={selectedPositionId}
                onChange={(e) => {
                  setSelectedPositionId(e.target.value);
                  setRules([]);
                  setShowRuleForm(false);
                  resetRuleForm();
                }}>
                <option value="">Select an overlay position…</option>
                {positions.map((p) => (
                  <option key={p.position_id} value={p.position_id}>
                    {p.full_name || `Position ${p.position_id}`}
                    {p.calculation_mode === 'quota_exempt' ? ' (quota-exempt)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {selectedPositionId && (
              <div className="flex gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                <span className="rounded-full bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-1">
                  {rules.length} rule{rules.length !== 1 ? 's' : ''}
                </span>
                {unapprovedCount > 0 && (
                  <span className="rounded-full bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-1 text-[rgb(var(--color-warning))]">
                    {unapprovedCount} pending approval
                  </span>
                )}
              </div>
            )}
          </div>

          {!selectedPositionId && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Select an overlay position above to view and configure its attribution rules.
                Overlay positions are typically quota-exempt specialists who support primary AEs on deals.
              </p>
            </div>
          )}

          {selectedPositionId && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Rules are evaluated most-specific-first (segment+BU &gt; segment-only &gt; BU-only &gt; catch-all).
                  Each rule must be approved before the engine uses it.
                </p>
                {!showRuleForm && (
                  <button type="button" className={btnPrimary}
                    onClick={() => {
                      resetRuleForm();
                      setRuleForm((prev) => ({ ...prev, overlay_position_id: selectedPositionId }));
                      setShowRuleForm(true);
                    }}>
                    + New rule
                  </button>
                )}
              </div>

              {/* Rule create form */}
              {showRuleForm && !editingRuleId && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
                  <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New attribution rule</h3>
                  {ruleFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                      {ruleFormError}
                    </div>
                  )}
                  {renderRuleFormFields({ ruleForm, updateRuleForm, components, segments, businessUnits, positions, inputClass, labelClass, disablePosition: true })}
                  <div className="flex items-center gap-2">
                    <button type="button" className={btnPrimary} onClick={createRule} disabled={ruleSaving}>
                      {ruleSaving ? 'Creating…' : 'Create rule'}
                    </button>
                    <button type="button" className={btnSecondary}
                      onClick={() => { setShowRuleForm(false); resetRuleForm(); }} disabled={ruleSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {rulesLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading rules…</p>}
              {rulesError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {rulesError}
                </div>
              )}

              {!rulesLoading && !rulesError && rules.length === 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No attribution rules for this position. Create a rule to define the attribution weight
                    that determines how much of each deal value is used to compute this rep's commission.
                  </p>
                </div>
              )}

              {sortedRules.length > 0 && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Scope</th>
                        <th className={thClass}>Weight</th>
                        <th className={thClass}>Plan component</th>
                        <th className={thClass}>Effective</th>
                        <th className={thClass}>Active</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass} aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRules.map((rule) => (
                        <tr key={rule.rule_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>
                            <div className="flex items-center gap-2">
                              <span>{scopeLabel(rule)}</span>
                              {specificityRank(rule) === 3 && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-tok-xs bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))] font-medium">
                                  Most specific
                                </span>
                              )}
                              {specificityRank(rule) === 1 && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-tok-xs bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]">
                                  Catch-all
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={tdClass + ' font-medium'}>{fmtPct(rule.attribution_weight)}</td>
                          <td className={tdClass}>{componentLabelById[String(rule.plan_component_id)] || rule.plan_component_id}</td>
                          <td className={tdClass}>
                            {fmtDate(rule.effective_from)}
                            {rule.effective_to ? ` → ${fmtDate(rule.effective_to)}` : ''}
                          </td>
                          <td className={tdClass}>{rule.is_active ? 'Yes' : 'No'}</td>
                          <td className={tdClass}><StatusPill status={deriveState(rule)} /></td>
                          <td className={tdClass}>
                            <button type="button"
                              className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                              onClick={() => loadRuleDetail(rule.rule_id)}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Results tab ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review overlay commission amounts computed for a calculation run.
            Each row shows the attributed deal value, the resolved commission rate,
            and the final overlay commission amount. Primary rep payouts are unaffected.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={labelClass}>
              Calculation run ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" className={inputClass} value={runIdInput}
                onChange={(e) => setRunIdInput(e.target.value)}
                placeholder="UUID from calculation run" />
            </label>
            <label className={labelClass}>
              Overlay position (optional)
              <select className={inputClass} value={overlayPosIdInput}
                onChange={(e) => setOverlayPosIdInput(e.target.value)}>
                <option value="">All overlay positions</option>
                {positions.map((p) => (
                  <option key={p.position_id} value={p.position_id}>
                    {p.full_name || `Position ${p.position_id}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <button type="button" className={btnPrimary} onClick={loadResults} disabled={resultsLoading}>
              {resultsLoading ? 'Loading…' : 'Load results'}
            </button>
          </div>
          {resultsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {resultsError}
            </div>
          )}
          {!resultsLoading && results.length === 0 && runIdInput && !resultsError && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
              No overlay commission results found for this run. No overlay positions may have had
              matching credits, or no approved attribution rules were in effect.
            </p>
          )}
          {results.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Overlay rep</th>
                    <th className={thClass}>Primary rep</th>
                    <th className={thClass}>Attributed deal value</th>
                    <th className={thClass}>Commission rate</th>
                    <th className={thClass}>Overlay commission</th>
                    <th className={thClass}>Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.overlay_result_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium'}>
                        {positionNameById[String(r.overlay_position_id)] || r.overlay_position_id}
                      </td>
                      <td className={tdClass}>
                        {positionNameById[String(r.primary_position_id)] || r.primary_position_id}
                      </td>
                      <td className={tdClass}>{fmtMoney(r.attributed_deal_value)}</td>
                      <td className={tdClass}>{fmtRate(r.overlay_commission_rate)}</td>
                      <td className={tdClass + ' font-medium'}>{fmtMoney(r.overlay_commission_amount)}</td>
                      <td className={tdClass}>{r.payout_currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Extracted rule form fields — used in create and edit contexts
// ---------------------------------------------------------------------------

function renderRuleFormFields({
  ruleForm, updateRuleForm, components, segments,
  businessUnits, positions, inputClass, labelClass, disablePosition,
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {!disablePosition && (
        <label className={labelClass + ' sm:col-span-2'}>
          Overlay position <span className="text-[rgb(var(--color-danger))]">*</span>
          <select className={inputClass} value={ruleForm.overlay_position_id}
            onChange={(e) => updateRuleForm('overlay_position_id', e.target.value)}>
            <option value="">Select an overlay position…</option>
            {positions.map((p) => (
              <option key={p.position_id} value={p.position_id}>
                {p.full_name || `Position ${p.position_id}`}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className={labelClass}>
        Plan component (rate source) <span className="text-[rgb(var(--color-danger))]">*</span>
        <select className={inputClass} value={ruleForm.plan_component_id}
          onChange={(e) => updateRuleForm('plan_component_id', e.target.value)}>
          <option value="">Select plan component…</option>
          {components.map((c) => (
            <option key={c.plan_component_id} value={c.plan_component_id}>
              {c.role_code || c.role_id || `Component ${c.plan_component_id}`}
              {c.segment_code ? ` — ${c.segment_code}` : ''}
            </option>
          ))}
        </select>
        <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          The overlay rep's rate table is sourced from this plan component
        </span>
      </label>

      <label className={labelClass}>
        Attribution weight <span className="text-[rgb(var(--color-danger))]">*</span>
        <input type="number" min="0.0001" max="1" step="0.0001" className={inputClass}
          value={ruleForm.attribution_weight}
          onChange={(e) => updateRuleForm('attribution_weight', e.target.value)}
          placeholder="e.g. 0.2500 for 25%" />
        <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Fraction of credit_reporting used to compute overlay commission. 0.25 = 25% of deal value.
        </span>
      </label>

      <label className={labelClass}>
        Effective from <span className="text-[rgb(var(--color-danger))]">*</span>
        <input type="date" className={inputClass}
          value={ruleForm.effective_from}
          onChange={(e) => updateRuleForm('effective_from', e.target.value)} />
      </label>

      <label className={labelClass}>
        Effective to
        <input type="date" className={inputClass}
          value={ruleForm.effective_to}
          onChange={(e) => updateRuleForm('effective_to', e.target.value)} />
      </label>

      {/* Scope selectors */}
      <div className="sm:col-span-2">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] mb-1">
          Scope
          <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
            Leave both blank for a catch-all rule. Narrower scope takes precedence.
          </span>
        </p>
      </div>

      <label className={labelClass}>
        Applies to segment
        <select className={inputClass} value={ruleForm.applies_to_segment_id}
          onChange={(e) => updateRuleForm('applies_to_segment_id', e.target.value)}>
          <option value="">All segments (NULL)</option>
          {segments.map((s) => (
            <option key={s.segment_id} value={s.segment_id}>
              {s.segment_name || `Segment ${s.segment_id}`}
            </option>
          ))}
        </select>
      </label>

      <label className={labelClass}>
        Applies to business unit
        <select className={inputClass} value={ruleForm.applies_to_bu_id}
          onChange={(e) => updateRuleForm('applies_to_bu_id', e.target.value)}>
          <option value="">All BUs (NULL)</option>
          {businessUnits.map((b) => (
            <option key={b.business_unit_id} value={b.business_unit_id}>
              {b.business_unit_name || `BU ${b.business_unit_id}`}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer select-none sm:col-span-2">
        <input type="checkbox"
          className="rounded border-[rgb(var(--color-border))]"
          checked={ruleForm.is_active}
          onChange={(e) => updateRuleForm('is_active', e.target.checked)} />
        Rule is active
      </label>
    </div>
  );
}
